import { Outlet, createRootRoute, useRouterState } from '@tanstack/react-router';
import { AppShell } from '@/components/layout/AppShell';
import { NotFoundPage } from '@/components/NotFoundPage';
import { RouteErrorComponent } from '@/components/RouteErrorComponent';
import { TourOverlay } from '@/features/tour/TourOverlay';
import { TourProvider } from '@/features/tour/TourProvider';
import { UploadTray } from '@/features/vault/components/UploadTray';
import { shouldRenderBare } from '@/lib/route-bare';

/**
 * Root route — wraps every page in the AppShell (header + subnav + main).
 *
 * TanStack Router file-based convention: `__root.tsx` is the layout root.
 * All other route files render inside its <Outlet />.
 *
 * `notFoundComponent` renders for any path that doesn't match a child
 * route. When that happens, the root's `matches` array contains only the
 * root match (length === 1) — we detect that and render the NotFoundPage
 * BARE, without AppShell, so the 404 stands alone with no navbar / subnav
 * to suggest there's a section hidden behind the missing URL.
 */
export const Route = createRootRoute({
  component: RootLayout,
  notFoundComponent: NotFoundPage,
  // Per-route error component: catches errors thrown by a route's
  // ``loader`` / ``beforeLoad`` / ``component``. Renders inside the
  // AppShell so the user can still navigate via the navbar. The
  // top-level <ErrorBoundary> in App.tsx is the next safety net —
  // anything that escapes the route boundary falls back to it.
  errorComponent: RouteErrorComponent,
});

function RootLayout() {
  // Render bare (no AppShell) for the 404 fallback (unmatched path OR a matched
  // route that threw notFound(), e.g. a missing / cross-tenant vault document)
  // and for /suspended — the latter must make no authed API calls so it can't
  // loop through the access_suspended redirect.
  const bare = useRouterState({
    select: (s) => shouldRenderBare(s.matches, s.location.pathname),
  });
  if (bare) return <Outlet />;
  return (
    <TourProvider>
      <AppShell>
        <Outlet />
      </AppShell>
      <TourOverlay />
      {/* Vault uploads run in the background; the tray follows the person to every page. */}
      <UploadTray />
    </TourProvider>
  );
}
